import React, { useState, useEffect, useRef } from 'react';
import { Settings, Save, RotateCcw, Clock, X, AlertCircle, CheckCircle, Link, Loader } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../apiConfig';

/**
 * Organization Settings Admin Panel
 *
 * PURPOSE: Allow admin users to configure org-specific business rules and thresholds
 *
 * FEATURES:
 * - 4-tier settings structure (Critical, Scoring, Distance, Route)
 * - Real-time validation
 * - Change history tracking
 * - Reset to defaults
 * - Save confirmation with change notes
 *
 * PERMISSIONS: Admin role only
 */

function useDraggable(enabled) {
    const [pos, setPos] = useState(null);
    const dragging = useRef(false);
    const offset   = useRef({ x: 0, y: 0 });
    const onMouseDown = (e) => {
        if (!enabled) return;
        if (e.target.closest('button')) return;
        dragging.current = true;
        const rect = e.currentTarget.closest('[data-draggable]').getBoundingClientRect();
        offset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        document.body.style.userSelect = 'none';
        const onMove = (ev) => { if (dragging.current) setPos({ x: ev.clientX - offset.current.x, y: ev.clientY - offset.current.y }); };
        const onUp   = () => { dragging.current = false; document.body.style.userSelect = ''; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };
    return { pos, headerProps: { onMouseDown } };
}

const extractErrorMessage = (err, fallback) => {
    const detail = err.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => e.msg || String(e)).join('; ');
    return fallback;
};

const OrgSettings = ({ currentUser, onClose }) => {
    const [settings, setSettings] = useState(null);
    const [originalSettings, setOriginalSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [changeNotes, setChangeNotes] = useState('');
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        if (currentUser?.role !== 'admin') {
            setError('Unauthorized: Admin access required');
            setLoading(false);
        } else {
            loadSettings();
        }
    }, [currentUser]);

    const loadSettings = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get(`${API_BASE_URL}/api/org-settings`);
            setSettings(response.data);
            setOriginalSettings(response.data);
            setLoading(false);
        } catch (err) {
            setError(extractErrorMessage(err, 'Failed to load settings'));
            setLoading(false);
        }
    };

    const loadHistory = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/org-settings/history`);
            setHistory(response.data);
            setShowHistory(true);
        } catch (err) {
            setError('Failed to load history');
        }
    };

    const handleChange = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
        setValidationErrors(prev => ({ ...prev, [field]: null }));
        setSuccess(null);
    };

    const validateSettings = () => {
        const errors = {};
        const weightSum =
            Number(settings.weight_priority || 0) +
            Number(settings.weight_revenue || 0) +
            Number(settings.weight_proximity || 0) +
            Number(settings.weight_opportunity || 0);
        if (Math.abs(weightSum - 1.0) > 0.01) {
            errors.weights = `Scoring weights must sum to 1.0 (currently: ${weightSum.toFixed(2)})`;
        }
        if (Number(settings.immediate_distance_max) >= Number(settings.near_distance_max)) {
            errors.distance_bands = 'Immediate distance must be less than Near distance';
        }
        if (Number(settings.near_distance_max) >= Number(settings.far_distance_max)) {
            errors.distance_bands = 'Near distance must be less than Far distance';
        }
        ['recent_contact_exclusion_days', 'visit_duplicate_prevention_days', 'overdue_visit_threshold_days', 'sales_cycle_length_days', 'territory_lead_limit'].forEach(field => {
            if (Number(settings[field]) <= 0) errors[field] = 'Must be positive';
        });
        if (Number(settings.light_traffic_delay_factor) < 1.0) errors.light_traffic_delay_factor = 'Must be >= 1.0';
        if (Number(settings.moderate_traffic_delay_factor) < Number(settings.light_traffic_delay_factor)) errors.traffic_factors = 'Moderate >= Light';
        if (Number(settings.heavy_traffic_delay_factor) < Number(settings.moderate_traffic_delay_factor)) errors.traffic_factors = 'Heavy >= Moderate';
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {
        if (!validateSettings()) { setError('Please fix validation errors before saving'); return; }
        try {
            setSaving(true);
            setError(null);
            const response = await axios.put(`${API_BASE_URL}/api/org-settings`, {
                settings,
                change_notes: changeNotes.trim() || null
            });
            setSettings(response.data);
            setOriginalSettings(response.data);
            setSuccess('Settings saved successfully');
            setChangeNotes('');
            setSaving(false);
        } catch (err) {
            setError(extractErrorMessage(err, 'Failed to save settings'));
            setSaving(false);
        }
    };

    const handleResetConfirm = async () => {
        setShowResetConfirm(false);
        try {
            setSaving(true);
            setError(null);
            const response = await axios.post(`${API_BASE_URL}/api/org-settings/reset`);
            setSettings(response.data);
            setOriginalSettings(response.data);
            setSuccess('Settings reset to defaults');
            setSaving(false);
        } catch (err) {
            setError(extractErrorMessage(err, 'Failed to reset settings'));
            setSaving(false);
        }
    };

    const hasChanges = () => {
        if (!settings || !originalSettings) return false;
        return JSON.stringify(settings) !== JSON.stringify(originalSettings);
    };

    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;
    const { pos, headerProps } = useDraggable(isDesktop);
    const panelStyle = pos && isDesktop ? { position: 'fixed', left: pos.x, top: pos.y, margin: 0, transform: 'none' } : {};

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div data-draggable style={panelStyle} className="bg-gray-900 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">

                {/* Header */}
                <div {...headerProps} className={`bg-gradient-to-r from-blue-900 to-purple-900 px-6 py-4 flex items-center justify-between flex-shrink-0 ${isDesktop ? 'cursor-move' : ''}`}>
                    <div className="flex items-center gap-3">
                        <Settings className="w-6 h-6 text-blue-300" />
                        <div>
                            <h1 className="text-xl font-bold text-white">Organization Settings</h1>
                            <p className="text-sm text-blue-200">Configure business rules and thresholds</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                        <X className="w-5 h-5 text-white" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {/* Mobile banner */}
                    <div className="md:hidden p-3 bg-blue-900/40 border border-blue-700 rounded-lg text-sm text-blue-200">
                        For the best experience, manage org settings on desktop.
                    </div>

                    {loading && (
                        <div className="flex items-center justify-center py-16">
                            <Loader className="w-8 h-8 text-blue-400 animate-spin" />
                        </div>
                    )}

                    {!loading && (
                        <>
                            {success && (
                                <div className="p-3 bg-green-900/50 border border-green-700 rounded-lg flex items-center gap-3">
                                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                                    <p className="text-green-300 text-sm">{success}</p>
                                </div>
                            )}
                            {error && (
                                <div className="p-3 bg-red-900/50 border border-red-700 rounded-lg flex items-center gap-3">
                                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                                    <p className="text-red-300 text-sm">{error}</p>
                                </div>
                            )}

                            {hasChanges() && (
                                <div className="p-3 bg-yellow-900/30 border border-yellow-700 rounded-lg">
                                    <label className="block text-xs font-semibold text-yellow-300 mb-1.5">
                                        Change Notes <span className="font-normal text-gray-400">(optional, for audit trail)</span>
                                    </label>
                                    <textarea
                                        value={changeNotes}
                                        onChange={(e) => setChangeNotes(e.target.value)}
                                        placeholder="Describe what you changed and why..."
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg text-sm placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                        rows={2}
                                    />
                                </div>
                            )}

                            {/* Settings Sections */}
                            <SettingsSection title="Tier 1: Critical Business Rules" description="Core filters and sales cycle configuration">
                                <SettingField label="Recent Contact Exclusion (days)" description="AI won't recommend leads contacted within this period" value={settings?.recent_contact_exclusion_days} onChange={(val) => handleChange('recent_contact_exclusion_days', val)} error={validationErrors.recent_contact_exclusion_days} />
                                <SettingField label="Visit Duplicate Prevention (days)" description="Prevent creating duplicate visits within this period" value={settings?.visit_duplicate_prevention_days} onChange={(val) => handleChange('visit_duplicate_prevention_days', val)} error={validationErrors.visit_duplicate_prevention_days} />
                                <SettingField label="Overdue Visit Threshold (days)" description="Visits older than this are flagged overdue" value={settings?.overdue_visit_threshold_days} onChange={(val) => handleChange('overdue_visit_threshold_days', val)} error={validationErrors.overdue_visit_threshold_days} />
                                <SettingField label="Sales Cycle Length (days)" description="Average sales cycle duration" value={settings?.sales_cycle_length_days} onChange={(val) => handleChange('sales_cycle_length_days', val)} error={validationErrors.sales_cycle_length_days} />
                                <SettingField label="Follow-up Cadence (days)" description="Standard follow-up interval" value={settings?.follow_up_cadence_days} onChange={(val) => handleChange('follow_up_cadence_days', val)} />
                                <SettingField label="Stale Lead Threshold (days)" description="Leads not contacted in this period are considered stale" value={settings?.stale_lead_threshold_days} onChange={(val) => handleChange('stale_lead_threshold_days', val)} />
                            </SettingsSection>

                            <SettingsSection title="Tier 2: Scoring & Prioritization" description="Lead scoring weights and thresholds">
                                {validationErrors.weights && (
                                    <div className="p-3 bg-red-900/40 border border-red-700 rounded text-red-300 text-sm">{validationErrors.weights}</div>
                                )}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <SettingField label="Priority Weight" value={settings?.weight_priority} onChange={(val) => handleChange('weight_priority', val)} step="0.01" min="0" max="1" />
                                    <SettingField label="Revenue Weight" value={settings?.weight_revenue} onChange={(val) => handleChange('weight_revenue', val)} step="0.01" min="0" max="1" />
                                    <SettingField label="Proximity Weight" value={settings?.weight_proximity} onChange={(val) => handleChange('weight_proximity', val)} step="0.01" min="0" max="1" />
                                    <SettingField label="Opportunity Weight" value={settings?.weight_opportunity} onChange={(val) => handleChange('weight_opportunity', val)} step="0.01" min="0" max="1" />
                                </div>
                                <div className="grid grid-cols-3 gap-4 mt-2">
                                    <SettingField label="High Priority Score" value={settings?.high_priority_score} onChange={(val) => handleChange('high_priority_score', val)} />
                                    <SettingField label="Medium Priority Score" value={settings?.medium_priority_score} onChange={(val) => handleChange('medium_priority_score', val)} />
                                    <SettingField label="Low Priority Score" value={settings?.low_priority_score} onChange={(val) => handleChange('low_priority_score', val)} />
                                </div>
                                <SettingField label="Revenue Normalization Divisor" description="Divisor for normalizing revenue to 0-100 score" value={settings?.revenue_normalization_divisor} onChange={(val) => handleChange('revenue_normalization_divisor', val)} />
                            </SettingsSection>

                            <SettingsSection title="Tier 3: Distance & Geography" description="Distance bands and thresholds (km)">
                                {validationErrors.distance_bands && (
                                    <div className="p-3 bg-red-900/40 border border-red-700 rounded text-red-300 text-sm">{validationErrors.distance_bands}</div>
                                )}
                                <div className="grid grid-cols-3 gap-4">
                                    <SettingField label="Immediate Max (km)" value={settings?.immediate_distance_max} onChange={(val) => handleChange('immediate_distance_max', val)} step="0.1" />
                                    <SettingField label="Near Max (km)" value={settings?.near_distance_max} onChange={(val) => handleChange('near_distance_max', val)} step="0.1" />
                                    <SettingField label="Far Max (km)" value={settings?.far_distance_max} onChange={(val) => handleChange('far_distance_max', val)} step="0.1" />
                                </div>
                                <SettingField label="Station Proximity Bonus" description="Score bonus for leads near train stations" value={settings?.station_proximity_bonus} onChange={(val) => handleChange('station_proximity_bonus', val)} />
                            </SettingsSection>

                            <SettingsSection title="Tier 4: Route Optimization" description="Route planning and traffic settings">
                                <SettingField label="Morning Route Max Distance (km)" value={settings?.morning_route_max_distance_km} onChange={(val) => handleChange('morning_route_max_distance_km', val)} step="0.1" />
                                <SettingField label="Average City Speed (km/h)" value={settings?.average_city_speed_kmh} onChange={(val) => handleChange('average_city_speed_kmh', val)} />
                                {validationErrors.traffic_factors && (
                                    <div className="p-3 bg-red-900/40 border border-red-700 rounded text-red-300 text-sm">{validationErrors.traffic_factors}</div>
                                )}
                                <div className="grid grid-cols-3 gap-4">
                                    <SettingField label="Light Traffic" value={settings?.light_traffic_delay_factor} onChange={(val) => handleChange('light_traffic_delay_factor', val)} step="0.01" min="1.0" error={validationErrors.light_traffic_delay_factor} />
                                    <SettingField label="Moderate Traffic" value={settings?.moderate_traffic_delay_factor} onChange={(val) => handleChange('moderate_traffic_delay_factor', val)} step="0.01" min="1.0" />
                                    <SettingField label="Heavy Traffic" value={settings?.heavy_traffic_delay_factor} onChange={(val) => handleChange('heavy_traffic_delay_factor', val)} step="0.01" min="1.0" />
                                </div>
                            </SettingsSection>

                            <SettingsSection title="Territory & Data Quality" description="Data limits and thresholds">
                                <SettingField label="Territory Lead Limit" description="Maximum leads to fetch per territory analysis" value={settings?.territory_lead_limit} onChange={(val) => handleChange('territory_lead_limit', val)} error={validationErrors.territory_lead_limit} />
                                <SettingField label="Briefing Max Recommendations" description="Maximum recommendations in daily briefing" value={settings?.briefing_max_recommendations} onChange={(val) => handleChange('briefing_max_recommendations', val)} />
                            </SettingsSection>

                            <SettingsSection title="Integrations" description="Enable third-party integrations for your organization">
                                <SettingToggle
                                    label="Google Calendar Sync"
                                    description="Allow salespeople to sync confirmed visit appointments to their personal Google Calendar. Each user must connect their own Google account after this is enabled."
                                    value={!!settings?.google_calendar_enabled}
                                    onChange={(val) => handleChange('google_calendar_enabled', val)}
                                />
                                {settings?.google_calendar_enabled && (
                                    <div className="flex items-start gap-2 p-3 bg-blue-900/30 border border-blue-700 rounded-lg text-xs text-blue-300">
                                        <Link className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-400" />
                                        <span>Enabled. Each team member can connect their own Google account under <strong className="text-blue-200">Profile → Integrations</strong>.</span>
                                    </div>
                                )}
                            </SettingsSection>
                        </>
                    )}
                </div>

                {/* Sticky Footer */}
                <div className="flex-shrink-0 bg-gray-900 border-t border-gray-700 px-6 py-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={loadHistory}
                            className="flex items-center gap-1.5 px-3 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 text-sm transition-colors"
                        >
                            <Clock className="w-4 h-4" />
                            History
                        </button>
                        <button
                            onClick={() => setShowResetConfirm(true)}
                            disabled={saving}
                            className="flex items-center gap-1.5 px-3 py-2 border border-red-700 text-red-400 rounded-lg hover:bg-red-900/30 text-sm transition-colors"
                        >
                            <RotateCcw className="w-4 h-4" />
                            Reset
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 text-sm transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={!hasChanges() || saving}
                            className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold transition-opacity"
                        >
                            {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            {saving ? 'Saving…' : 'Save Changes'}
                        </button>
                    </div>
                </div>

                {/* Reset Confirmation Modal */}
                {showResetConfirm && (
                    <ResetConfirmModal onConfirm={handleResetConfirm} onCancel={() => setShowResetConfirm(false)} />
                )}

                {/* History Modal */}
                {showHistory && (
                    <HistoryModal history={history} onClose={() => setShowHistory(false)} />
                )}
            </div>
        </div>
    );
};

const SettingsSection = ({ title, description, children }) => (
    <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
        <div className="mb-4">
            <h2 className="text-sm font-bold text-white">{title}</h2>
            <p className="text-xs text-gray-400">{description}</p>
        </div>
        <div className="space-y-4">{children}</div>
    </div>
);

const SettingToggle = ({ label, description, value, onChange }) => (
    <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
            <label className="block text-sm font-semibold text-white mb-0.5">{label}</label>
            {description && <p className="text-xs text-gray-400">{description}</p>}
        </div>
        <button
            type="button"
            onClick={() => onChange(!value)}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-gray-800 ${value ? 'bg-blue-600' : 'bg-gray-600'}`}
            role="switch"
            aria-checked={value}
        >
            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${value ? 'translate-x-5' : 'translate-x-0'}`} />
        </button>
    </div>
);

const SettingField = ({ label, description, value, onChange, type = "number", step, min, max, error }) => (
    <div>
        <label className="block text-xs font-semibold text-gray-300 mb-1">{label}</label>
        {description && <p className="text-xs text-gray-500 mb-1.5">{description}</p>}
        <input
            type={type}
            value={value || ''}
            onChange={(e) => onChange(type === 'number' ? Number(e.target.value) : e.target.value)}
            step={step}
            min={min}
            max={max}
            className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none ${
                error ? 'border-red-500' : 'border-gray-600'
            }`}
        />
        {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
);

const ResetConfirmModal = ({ onConfirm, onCancel }) => (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[60]">
        <div className="bg-gray-800 border border-gray-700 rounded-xl max-w-sm w-full shadow-2xl p-6">
            <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                Reset to Defaults?
            </h2>
            <p className="text-gray-400 text-sm mb-5">
                This will reset <strong className="text-white">all settings</strong> to their default values. This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
                <button onClick={onCancel} className="px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 text-sm">Cancel</button>
                <button onClick={onConfirm} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold">Yes, Reset</button>
            </div>
        </div>
    </div>
);

const FIELD_LABELS = {
    recent_contact_exclusion_days: 'Recent Contact Exclusion (days)',
    visit_duplicate_prevention_days: 'Duplicate Visit Prevention (days)',
    overdue_visit_threshold_days: 'Overdue Visit Threshold (days)',
    sales_cycle_length_days: 'Sales Cycle Length (days)',
    follow_up_cadence_days: 'Follow-up Cadence (days)',
    stale_lead_threshold_days: 'Stale Lead Threshold (days)',
    customer_inactive_threshold_days: 'Customer Inactive Threshold (days)',
    weight_priority: 'Priority Weight', weight_revenue: 'Revenue Weight',
    weight_proximity: 'Proximity Weight', weight_opportunity: 'Opportunity Weight',
    high_priority_score: 'High Priority Score', medium_priority_score: 'Medium Priority Score',
    low_priority_score: 'Low Priority Score', revenue_normalization_divisor: 'Revenue Normalization Divisor',
    high_revenue_threshold: 'High Revenue Threshold',
    immediate_distance_max: 'Immediate Distance Max (km)', near_distance_max: 'Near Distance Max (km)',
    far_distance_max: 'Far Distance Max (km)', very_close_threshold_km: 'Very Close Threshold (km)',
    station_proximity_bonus: 'Station Proximity Bonus',
    morning_route_max_distance_km: 'Morning Route Max Distance (km)',
    average_city_speed_kmh: 'Average City Speed (km/h)',
    light_traffic_delay_factor: 'Light Traffic Delay Factor',
    moderate_traffic_delay_factor: 'Moderate Traffic Delay Factor',
    heavy_traffic_delay_factor: 'Heavy Traffic Delay Factor',
    google_calendar_enabled: 'Google Calendar Integration',
    territory_lead_limit: 'Territory Lead Limit',
    briefing_max_recommendations: 'Max AI Recommendations',
    briefing_scored_pool_size: 'Scored Pool Size',
    cluster_visit_threshold: 'Cluster Visit Threshold',
    high_revenue_area_threshold: 'High Revenue Area Threshold',
};

const formatFieldName = (field) => FIELD_LABELS[field] || field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const HistoryModal = ({ history, onClose }) => (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[60]">
        <div className="bg-gray-800 border border-gray-700 rounded-xl max-w-3xl w-full max-h-[80vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 flex-shrink-0">
                <h2 className="text-lg font-bold text-white">Change History</h2>
                <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-full">
                    <X className="w-5 h-5 text-gray-400" />
                </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
                {history.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No change history available</p>
                ) : (
                    history.map((record) => (
                        <div key={record.id} className="border border-gray-600 bg-gray-700/30 rounded-lg p-4">
                            <div className="flex items-start justify-between mb-2">
                                <div>
                                    <p className="font-semibold text-white text-sm">Version {record.settings_version}</p>
                                    <p className="text-xs text-gray-400">{new Date(record.changed_at).toLocaleString()}</p>
                                </div>
                                <span className="text-xs text-gray-400">{record.changed_by_email}</span>
                            </div>
                            {record.changed_fields?.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                    {record.changed_fields.map((field, idx) => (
                                        <span key={idx} className="px-2 py-0.5 bg-blue-900/40 border border-blue-700 text-blue-300 text-xs rounded">
                                            {formatFieldName(field)}
                                        </span>
                                    ))}
                                </div>
                            )}
                            {record.change_reason && (
                                <p className="text-xs text-gray-400 italic">"{record.change_reason}"</p>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    </div>
);

export default OrgSettings;
